import { useCallback, useEffect, useState } from 'react';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';

import { useNavigateToTop } from '@src/hooks/useNavigateToTop';

// Product screenshots. Dark-theme captures of the live app at 1920x1080,
// so every slide fills the 16:9 frame below without cropping.
import businessEntities from '@src/assets/screenshots/business-entities.webp';
import businessProcesses from '@src/assets/screenshots/business-processes.webp';
import cmdbHygieneIdentity from '@src/assets/screenshots/cmdb-hygiene-identity.webp';
import cmdbHygieneOverview from '@src/assets/screenshots/cmdb-hygiene-overview.webp';
import cmdbHygieneSoftware from '@src/assets/screenshots/cmdb-hygiene-software.webp';
import cmdbShadowIt from '@src/assets/screenshots/cmdb-shadow-it.webp';
import documentsDashboard from '@src/assets/screenshots/documents-dashboard.webp';
import grcMonteCarlo from '@src/assets/screenshots/grc-monte-carlo.webp';
import grcRiskAnalytics from '@src/assets/screenshots/grc-risk-analytics.webp';
import iamPeopleEntitlement from '@src/assets/screenshots/iam-people-entitlement.webp';
import itsmDashboard from '@src/assets/screenshots/itsm-dashboard.webp';
import itsmTickets from '@src/assets/screenshots/itsm-tickets.webp';
import pentestDashboard from '@src/assets/screenshots/pentest-dashboard.webp';
import pentestPhishingEvents from '@src/assets/screenshots/pentest-phishing-events.webp';
import platformDashboard from '@src/assets/screenshots/platform-dashboard.webp';
import resilienceBcrpDashboard from '@src/assets/screenshots/resilience-bcrp-dashboard.webp';
import xdrDashboard from '@src/assets/screenshots/xdr-dashboard.webp';
import xdrSecurityAlerts from '@src/assets/screenshots/xdr-security-alerts.webp';

const PRODUCTS = [
  {
    id: 'dashboard',
    name: 'Unified Dashboard',
    shortName: 'Platform',
    description:
      'Risk, continuity, assets and documents on a single pane of glass',
    image: platformDashboard,
    path: '/platform/grc',
    color: '#1e638d',
  },
  {
    id: 'xdr/dashboard',
    name: 'XDR Security Dashboard',
    shortName: 'XDR',
    description: 'Live detection metrics with full MITRE ATT&CK coverage',
    image: xdrDashboard,
    path: '/platform/xdr',
    color: '#dc2626',
  },
  {
    id: 'cmdb/it-hygiene',
    name: 'IT Hygiene Dashboard',
    shortName: 'CMDB',
    description: 'Infrastructure health across every tracked asset category',
    image: cmdbHygieneOverview,
    path: '/platform/cmdb',
    color: '#0891b2',
  },
  {
    id: 'grc/risk-register/multi-sim',
    name: 'Multi-Scenario Monte Carlo',
    shortName: 'GRC',
    description: 'Run a portfolio of FAIR scenarios and compare exposure shapes',
    image: grcMonteCarlo,
    path: '/platform/grc',
    color: '#1e638d',
  },
  {
    id: 'pentest/dashboard',
    name: 'Penetration Testing',
    shortName: 'Pentest',
    description: 'Engagement tracking with severity and remediation status',
    image: pentestDashboard,
    path: '/platform/pentest',
    color: '#ea580c',
  },
  {
    id: 'iam/entitlements',
    name: 'Identity Entitlements',
    shortName: 'IAM',
    description: 'Who has access to what, and how they were granted it',
    image: iamPeopleEntitlement,
    path: '/platform/iam',
    color: '#7c3aed',
  },
  {
    id: 'xdr/security-alerts',
    name: 'Security Alerts',
    shortName: 'XDR',
    description: 'Triage queue enriched with assets, identities and TTPs',
    image: xdrSecurityAlerts,
    path: '/platform/xdr',
    color: '#dc2626',
  },
  {
    id: 'itsm/dashboard',
    name: 'IT Service Management',
    shortName: 'ITSM',
    description: 'Ticket volume, SLA attainment and change throughput',
    image: itsmDashboard,
    path: '/platform/service-desk',
    color: '#f59e0b',
  },
  {
    id: 'business/bcrp',
    name: 'Continuity & Recovery',
    shortName: 'BCRP',
    description: 'BCP and DR plans with live testing compliance',
    image: resilienceBcrpDashboard,
    path: '/platform/resilience',
    color: '#6366f1',
  },
  {
    id: 'cmdb/identity',
    name: 'Identity Hygiene',
    shortName: 'CMDB',
    description: 'Every identity, its source system and privilege level',
    image: cmdbHygieneIdentity,
    path: '/platform/cmdb',
    color: '#0891b2',
  },
  {
    id: 'grc/risk-register/dashboard',
    name: 'Risk Analytics',
    shortName: 'GRC',
    description: 'Control strength and expected annual loss across the portfolio',
    image: grcRiskAnalytics,
    path: '/platform/grc',
    color: '#1e638d',
  },
  {
    id: 'pentest/phishing',
    name: 'Phishing Campaigns',
    shortName: 'Pentest',
    description: 'Social engineering campaigns with per-target outcomes',
    image: pentestPhishingEvents,
    path: '/platform/pentest',
    color: '#ea580c',
  },
  {
    id: 'documents',
    name: 'Document Management',
    shortName: 'Documents',
    description: 'Classified, owned and entity-scoped document library',
    image: documentsDashboard,
    path: '/platform/documents',
    color: '#059669',
  },
  {
    id: 'business/processes',
    name: 'Business Processes',
    shortName: 'Business',
    description: 'Processes mapped to the services and assets behind them',
    image: businessProcesses,
    path: '/platform/business',
    color: '#8b5cf6',
  },
  {
    id: 'cmdb/software',
    name: 'Software Inventory',
    shortName: 'CMDB',
    description: 'Installed software, versions and end-of-life exposure',
    image: cmdbHygieneSoftware,
    path: '/platform/cmdb',
    color: '#0891b2',
  },
  {
    id: 'itsm/tickets',
    name: 'Service Desk Tickets',
    shortName: 'ITSM',
    description: 'Incidents, requests and changes in one queue',
    image: itsmTickets,
    path: '/platform/service-desk',
    color: '#f59e0b',
  },
  {
    id: 'business/entities',
    name: 'Entity Hierarchy',
    shortName: 'Business',
    description: 'Multi-entity structure that scopes every other module',
    image: businessEntities,
    path: '/platform/business',
    color: '#8b5cf6',
  },
  {
    id: 'cmdb/shadow-it',
    name: 'Shadow IT Discovery',
    shortName: 'CMDB',
    description: 'Unsanctioned services surfaced from real usage telemetry',
    image: cmdbShadowIt,
    path: '/platform/cmdb',
    color: '#0891b2',
  },
];

const AUTO_SCROLL_INTERVAL = 4000; // 4 seconds

export function PlatformCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [direction, setDirection] = useState(1);
  const navigateToTop = useNavigateToTop();

  const goToNext = useCallback(() => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % PRODUCTS.length);
  }, []);

  const goToPrev = useCallback(() => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + PRODUCTS.length) % PRODUCTS.length);
  }, []);

  const goToSlide = useCallback((index) => {
    setDirection(index > currentIndex ? 1 : -1);
    setCurrentIndex(index);
  }, [currentIndex]);

  // Auto-scroll effect
  useEffect(() => {
    if (isPaused) {
      return;
    }

    const interval = setInterval(goToNext, AUTO_SCROLL_INTERVAL);
    return () => clearInterval(interval);
  }, [isPaused, goToNext]);

  // Preload only the slides either side of the current one. Preloading all of
  // them on mount pulled the entire deck down before the page was interactive,
  // for a carousel most visitors never advance past the first slide or two.
  useEffect(() => {
    const neighbours = [
      (currentIndex + 1) % PRODUCTS.length,
      (currentIndex - 1 + PRODUCTS.length) % PRODUCTS.length,
    ];

    neighbours.forEach((index) => {
      const img = new Image();
      img.src = PRODUCTS[index].image;
    });
  }, [currentIndex]);

  const currentProduct = PRODUCTS[currentIndex];

  const slideVariants = {
    enter: (direction) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.95,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (direction) => ({
      x: direction > 0 ? -300 : 300,
      opacity: 0,
      scale: 0.95,
    }),
  };

  return (
    <section className="py-20 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          viewport={{ once: true }}
          className="text-center mb-12"
          transition={{ duration: 0.6 }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            See BOSSS In Action
          </h2>

          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            One platform, ten integrated modules. Explore how BOSSS transforms
            cybersecurity operations.
          </p>
        </motion.div>

        {/* Carousel Container */}
        <div
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Navigation Arrows */}
          <button
            aria-label="Previous product"
            className="absolute left-0 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/90 shadow-lg hover:bg-white transition-all duration-200 hover:scale-110 -translate-x-1/2 lg:translate-x-0"
            onClick={goToPrev}
          >
            <ChevronLeft className="w-6 h-6 text-gray-700" />
          </button>

          <button
            aria-label="Next product"
            className="absolute right-0 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full bg-white/90 shadow-lg hover:bg-white transition-all duration-200 hover:scale-110 translate-x-1/2 lg:translate-x-0"
            onClick={goToNext}
          >
            <ChevronRight className="w-6 h-6 text-gray-700" />
          </button>

          {/* Main Carousel */}
          <div className="relative mx-8 lg:mx-16">
            {/* Product Info Bar */}
            <motion.div
              key={`info-${currentIndex}`}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              initial={{ opacity: 0, y: -10 }}
              className="flex items-center justify-between mb-6"
            >
              <div>
                <div className="flex items-center gap-3">
                  <span
                    style={{ backgroundColor: currentProduct.color }}
                    className="px-3 py-1 rounded-full text-white text-sm font-semibold"
                  >
                    {currentProduct.shortName}
                  </span>

                  <h3 className="text-xl lg:text-2xl font-bold text-gray-900">
                    {currentProduct.name}
                  </h3>
                </div>

                <p className="text-gray-600 mt-1 hidden sm:block">
                  {currentProduct.description}
                </p>
              </div>

              <button
                style={{ backgroundColor: currentProduct.color }}
                className="px-4 py-2 rounded-lg text-white font-medium transition-all duration-200 hover:scale-105 hover:shadow-lg hidden sm:block"
                onClick={() => navigateToTop(currentProduct.path)}
              >
                Learn More
              </button>
            </motion.div>

            {/* Browser Frame */}
            <div
              className="rounded-xl overflow-hidden shadow-2xl cursor-pointer transition-transform duration-300 hover:scale-[1.01]"
              onClick={() => navigateToTop(currentProduct.path)}
            >
              {/* Browser Header */}
              <div
                style={{ backgroundColor: '#1e293b' }}
                className="px-4 py-3 flex items-center gap-2"
              >
                <div className="flex gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500" />
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                </div>

                <div className="flex-1 mx-4">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    key={`url-${currentIndex}`}
                    className="bg-slate-700 rounded-md px-4 py-1.5 text-sm text-slate-300 text-center max-w-md mx-auto"
                  >
                    app.secureonelabs.com/
                    {currentProduct.id}
                  </motion.div>
                </div>
              </div>

              {/* Screenshot Container */}
              {/* Matches the screenshots' own chrome, so the gap either side of
                  a slide mid-transition reads as part of the frame. */}
              <div
                style={{ minHeight: 'auto' }}
                className="relative bg-slate-900 overflow-hidden aspect-4/3 sm:aspect-video"
              >
                <AnimatePresence initial={false} mode="popLayout" custom={direction}>
                  <motion.img
                    exit="exit"
                    width={1920}
                    height={1080}
                    initial="enter"
                    animate="center"
                    key={currentIndex}
                    custom={direction}
                    variants={slideVariants}
                    src={currentProduct.image}
                    alt={`${currentProduct.name} dashboard`}
                    className="w-full h-full object-cover object-top"
                    transition={{
                      x: { type: 'spring', stiffness: 300, damping: 30 },
                      opacity: { duration: 0.2 },
                      scale: { duration: 0.2 },
                    }}
                  />
                </AnimatePresence>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-6 h-1 bg-gray-200 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: '0%' }}
                className="h-full rounded-full"
                key={`progress-${currentIndex}-${isPaused}`}
                style={{ backgroundColor: currentProduct.color }}
                animate={{ width: isPaused ? undefined : '100%' }}
                transition={{
                  duration: isPaused ? 0 : AUTO_SCROLL_INTERVAL / 1000,
                  ease: 'linear',
                }}
              />
            </div>
          </div>

          {/* Navigation Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {PRODUCTS.map((product, index) => (
              <button
                key={product.id}
                aria-label={`Go to ${product.name}`}
                style={index === currentIndex ? { backgroundColor: product.color } : {}}
                className={`relative w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex
                    ? 'scale-125'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                onClick={() => goToSlide(index)}
              />
            ))}
          </div>

          {/* Product Quick Links - Unique products only */}
          <div className="flex flex-wrap justify-center gap-2 mt-6">
            {(() => {
              const uniqueProducts = [];
              const seen = new Set();
              PRODUCTS.forEach((product, index) => {
                if (!seen.has(product.shortName)) {
                  seen.add(product.shortName);
                  uniqueProducts.push({ ...product, firstIndex: index });
                }
              });
              return uniqueProducts.map((product) => {
                const isActive = PRODUCTS[currentIndex].shortName === product.shortName;
                return (
                  <button
                    key={product.shortName}
                    style={isActive ? { backgroundColor: product.color } : {}}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? 'text-white shadow-md'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    onClick={() => goToSlide(product.firstIndex)}
                  >
                    {product.shortName}
                  </button>
                );
              });
            })()}
          </div>
        </div>
      </div>
    </section>
  );
}

export default PlatformCarousel;
